import { Type } from 'class-transformer';
import {
  type ClinicalActionRemoval,
  type ClinicalActionStatus,
  DermloopClinicalActionTypeEnum,
} from '@dermloop/static-values';
import {
  ArrayNotEmpty,
  IsArray,
  IsDefined,
  IsEmpty,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { FormSubmissionBlobDto } from './form-submission.dtos';

export class CreateClinicalActionOccurrenceDto {
  @IsUUID()
  formTemplateVersionId: string;

  @IsString()
  @Matches(/^[a-z0-9]+(-[a-z0-9]+)*$/)
  optionValue: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => FormSubmissionBlobDto)
  parameters: FormSubmissionBlobDto;

  @IsEmpty()
  actionType?: never;
}

/** `POST conditions/:conditionId/clinical-actions`: applied together or not at all. */
export class CreateClinicalActionsDto {
  /** Required unless the save removes actions; validated whenever supplied. */
  @ValidateIf(
    (dto: CreateClinicalActionsDto, actions) =>
      actions != null || !dto.removeActionIds?.length,
  )
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CreateClinicalActionOccurrenceDto)
  actions?: CreateClinicalActionOccurrenceDto[];

  /** Actions of this case to cancel (an unanswered tele-referral) or remove (a record-only action). */
  @ValidateIf(
    (dto: CreateClinicalActionsDto, ids) => ids != null || !dto.actions?.length,
  )
  @IsArray()
  @ArrayNotEmpty()
  @IsUUID('all', { each: true })
  removeActionIds?: string[];

  /** The Epic encounter the actions are taken in; their referrals wait for that encounter's signed order. */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  encounterFhirReference?: string;
}

export interface ConfiguredClinicalActionSelectionDto extends Omit<
  CreateClinicalActionOccurrenceDto,
  'actionType'
> {
  actionType: Exclude<
    DermloopClinicalActionTypeEnum,
    | DermloopClinicalActionTypeEnum.Referral
    | DermloopClinicalActionTypeEnum.NoAction
  >;
  label: Record<string, string>;
  disposition?: Record<string, string>;
}

/** One explicit clinical action of a condition, as `GET conditions/:conditionId/clinical-actions` reads it. */
export interface ClinicalActionDto {
  id: string;
  conditionId: string;
  configurationVersionId: string;
  optionValue: string;
  actionType: ConfiguredClinicalActionSelectionDto['actionType'];
  label: Record<string, string>;
  disposition?: Record<string, string>;
  parameters: FormSubmissionBlobDto;
  /** The option's parameter form, to render `parameters` with. */
  parameterDocument?: object;
  /** The completed clinical information the action was based on. */
  conditionTriageId: string;
  clinicalFormSubmissionId: string;
  clinical: FormSubmissionBlobDto;
  referralId: string | null;
  status: ClinicalActionStatus;
  /** What removing it would do, or `null` when it cannot be removed. */
  removal: ClinicalActionRemoval | null;
  actorId: string;
  actorName?: string;
  created: Date;
}
